import { getBuild, getBuilds, saveBuild, deleteBuild } from '@/api/builds';
import { Build, BuildState } from '@/types/build';
import { RootState } from '@/types/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import _ from 'lodash';

type BuildGetter = GetterTree<BuildState, RootState>;

const state: BuildState = {
  builds: [],
  selected: null,
  live: {
    id: null,
    user_id: '',
    clear_data: 0,
    clone_repo: 0,
    build_menus: 0,
    build_content: 0,
    build_pages: 0,
    s3_sync: 0,
    s3_redirects: 0,
    invalidate_cdn: 0,
    in_progress: 0,
    complete: 0,
    failed: 0,
    current_content: '',
    total_count: 0,
    current_count: 0,
    total_partner_count: 0,
    current_partner_count: 0,
    current_partner: '',
    partners_clear_data: 0,
    partners_build_menus: 0,
    partners_build_content: 0,
    partners_build_pages: 0,
    partners_in_progress: 0,
    partners_complete: 0,
    partners_failed: 0,
    completed_all_steps: 0,
    invalidation_id: null,
    created_at: '',
    updated_at: '',
    user: null,
  },
  queueFail: false,
  showSummaryPanel: null,
  showMainSitePanel: null,
  showPartnerSitePanel: null,
};

const getters: BuildGetter = {
  builds: state => _.orderBy(state.builds, ['created_at'], ['desc']),
  selected: state => state.selected,
  live: state => state.live,
  queueFail: state => state.queueFail,
  showSummaryPanel: state => state.showSummaryPanel,
  showMainSitePanel: state => state.showMainSitePanel,
  showPartnerSitePanel: state => state.showPartnerSitePanel,
};

const actions: ActionTree<BuildState, RootState> = {
  async getBuild({ commit }, id: string): Promise<void> {
    commit('setBuild', await getBuild(id));
  },

  async getBuilds({ commit }): Promise<void> {
    commit('setBuilds', await getBuilds());
  },

  async saveBuild({ commit }, build) {
    const item = await saveBuild(build);
    if (_.isUndefined(item)) {
      commit('setQueueToFail');
    }
    commit('updateBuild', item);
  },

  async saveLiveBuild({ commit }, build) {
    commit('updateLiveBuild', build);
  },

  async deleteBuild({ commit }, build) {
    await deleteBuild(build);
  },
};

const mutations: MutationTree<BuildState> = {
  newLiveBuild(state: BuildState) {
    state.live = {
      id: null,
      user_id: '',
      clear_data: 0,
      clone_repo: 0,
      build_menus: 0,
      build_content: 0,
      build_pages: 0,
      s3_sync: 0,
      s3_redirects: 0,
      invalidate_cdn: 0,
      in_progress: 0,
      complete: 0,
      failed: 0,
      current_content: '',
      total_count: 0,
      current_count: 0,
      total_partner_count: 0,
      current_partner_count: 0,
      current_partner: '',
      partners_clear_data: 0,
      partners_build_menus: 0,
      partners_build_content: 0,
      partners_build_pages: 0,
      partners_in_progress: 0,
      partners_complete: 0,
      partners_failed: 0,
      completed_all_steps: 0,
      invalidation_id: null,
      created_at: '',
      updated_at: '',
      user: null,
    };
  },

  setBuild(state: BuildState, build: Build) {
    state.selected = build;
  },

  setBuilds(state: BuildState, buildList: Build[]) {
    state.builds = buildList;
    if (buildList.length > 0) {
      const latestBuild = _.orderBy(buildList, ['created_at'], ['desc']);
      state.live = latestBuild[0];
      state.showSummaryPanel = state.live.build_menus;
      state.showMainSitePanel =
        state.live.invalidate_cdn === 1 && state.live.complete === 1 ? 1 : 0;
      state.showPartnerSitePanel = state.live.completed_all_steps;
    }
  },

  updateBuild(state: BuildState, updatedBuild: Build) {
    state.selected = updatedBuild;
  },

  setQueueToFail(state: BuildState) {
    state.queueFail = true;
  },

  setQueueToBonza(state: BuildState) {
    state.queueFail = false;
  },

  updateShowSummaryPanel(state: BuildState, show) {
    state.showSummaryPanel = show;
  },

  updateShowMainPanel(state: BuildState, show) {
    state.showMainSitePanel = show;
  },

  updateShowPartnerPanel(state: BuildState, show) {
    state.showPartnerSitePanel = show;
  },

  updateLiveBuild(state: BuildState, updatedLiveBuild: Build) {
    state.live = updatedLiveBuild;
    const index = state.builds.findIndex(
      (build: Build) => build.id === updatedLiveBuild.id,
    );
    if (index >= 0) {
      _.merge(state.builds[index], updatedLiveBuild);
    } else {
      state.builds.push(updatedLiveBuild);
    }
  },
};

const builds: Module<BuildState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};

export default builds;
